import { db } from '../database/schema.js'
import type { FieldLogEntry, FieldLogEntryType } from '../schemas/field-log-entry.js'
import { OfflineFirstStorageService, type StorageResult } from './base-storage.js'
import { edgeService, nodeRef } from '../services/edge-service.js'
import { flagFieldLogEntry } from '../services/moderation/moderation-api.js'
import type { JSONContent } from '../lib/tiptap-config.js'

export class FieldLogEntryStorage extends OfflineFirstStorageService<FieldLogEntry> {
  constructor() {
    super(db.fieldLogEntries, 'fieldLogEntry')
  }

  async getEntriesForPlan(planId: string): Promise<FieldLogEntry[]> {
    const entries = await db.fieldLogEntries.where('planId').equals(planId).toArray()
    return entries.sort((a, b) => (b.createdAt > a.createdAt ? 1 : -1))
  }

  async createEntry(data: {
    planId: string
    type: FieldLogEntryType
    content: JSONContent
    createdBy: string
    location?: { coordinates: [number, number]; name?: string }
  }): Promise<StorageResult<FieldLogEntry>> {
    const result = await this.create({
      ...data,
      flaggedBy: [],
      rootType: 'plan',
      rootId: data.planId,
    } as Omit<FieldLogEntry, 'id' | 'createdAt' | 'updatedAt'>)

    if (result.success && result.data) {
      await edgeService.create({
        subject: nodeRef('plan', data.planId),
        predicate: 'has-field-log-entry',
        object: nodeRef('fieldLogEntry', result.data.id),
      })
    }

    return result
  }

  async updateEntry(
    id: string,
    updates: { type?: FieldLogEntryType; content?: JSONContent }
  ): Promise<StorageResult<FieldLogEntry>> {
    return this.update(id, updates as Partial<Omit<FieldLogEntry, 'id' | 'createdAt'>>)
  }

  async deleteEntry(id: string): Promise<StorageResult<void>> {
    const result = await this.deleteById(id)
    if (result.success) {
      this.createSyncEvent(id, 'delete')
    }
    return result
  }

  async flagEntry(entryId: string, userId: string): Promise<StorageResult<FieldLogEntry>> {
    const entry = await this.table.get(entryId)
    if (!entry) {
      return {
        success: false,
        error: {
          code: 'NOT_FOUND',
          message: 'Entry not found',
          retryable: false,
        },
      }
    }

    const flaggedBy = entry.flaggedBy.includes(userId)
      ? entry.flaggedBy
      : [...entry.flaggedBy, userId]

    const result = await this.update(entryId, { flaggedBy } as Partial<
      Omit<FieldLogEntry, 'id' | 'createdAt'>
    >)

    if (result.success) {
      flagFieldLogEntry(entry.planId, entryId).catch(() => {})
    }

    return result
  }

  async unflagEntry(entryId: string, userId: string): Promise<StorageResult<FieldLogEntry>> {
    const entry = await this.table.get(entryId)
    if (!entry) {
      return {
        success: false,
        error: {
          code: 'NOT_FOUND',
          message: 'Entry not found',
          retryable: false,
        },
      }
    }

    const flaggedBy = entry.flaggedBy.filter(id => id !== userId)
    return this.update(entryId, { flaggedBy } as Partial<Omit<FieldLogEntry, 'id' | 'createdAt'>>)
  }
}

export const fieldLogEntryStorage = new FieldLogEntryStorage()
